/**
 * @fileoverview Platform-wide ArcMosaico theme override (AR-797)
 * @description Merges the arcmosaico.ai branding (Inter typography, indigo
 * #5B4FEB primary, arc link color) on top of the legacy Mosaico theme.
 * Applied only when the `arc-mosaico-project` flag is on — legacy schools
 * keep MosaicoTheme untouched.
 *
 * Adding a new brand (e.g. Novalumi) — with the current structure:
 *   1. Duplicate the "== Design tokens ==" block below into a
 *      NovalumiTheme.ts with the new palette/font, plus a sibling
 *      novalumi-branding.css (see themes/arc-branding.css header).
 *   2. Logo: components/ArcLogo is the SINGLE logo render point in the SPA
 *      (used by SidebarMenu and ArcAuthHeader) — swap the asset/component
 *      there under the new brand's flag.
 *   3. Gate the theme merge in app.tsx behind the new brand's own flag.
 *   Legacy (MosaicoTheme) stays untouched by design.
 */

import { MantineThemeOverride, Tuple } from '@mantine/core'
import { keyframes } from '@mantine/styles'

/* ============================== Design tokens ==============================
 * Single source of truth for the Arc brand on the Mantine side. The raw-CSS
 * twin values live in themes/arc-branding.css — when adjusting a color here,
 * update its twin there so Mantine components and plain CSS stay in sync.
 * ========================================================================== */

/**
 * Lives here (not in ArcAuthPage) so the root bundle can theme without
 * pulling the auth-screen styled-components in; ArcAuthPage re-exports it.
 */
export const ARC_FONT_FAMILY = "'Inter', 'Malva', -apple-system, BlinkMacSystemFont, sans-serif"

/** #5B4FEB ramp — index 6 is the site primary, 7 its hover (#4A3FD4) */
export const ARC_PURPLE_SHADES: Tuple<string, 10> = [
  '#F0EFFE',
  '#E3E0FD',
  '#C8C2FB',
  '#ACA5F8',
  '#8F86F3',
  '#7269EF',
  '#5B4FEB',
  '#4A3FD4',
  '#3B32B0',
  '#2C258C',
]

/** Index of the filled/primary tone (#5B4FEB) inside ARC_PURPLE_SHADES */
export const ARC_PRIMARY_SHADE_INDEX = 6

/** Hero wash from arcmosaico.ai (.hero) — lilac → peach → base */
export const ARC_HERO_WASH =
  'radial-gradient(ellipse 90% 70% at 60% 45%, #EDE9FE 0%, #FFF1E6 45%, #F7F8FC 80%)'

/** Signature gradient for AI actions (site "create with AI" affordances) */
export const ARC_AI_GRADIENT = 'linear-gradient(135deg, #5B4FEB 0%, #A855F7 100%)'
export const ARC_AI_GRADIENT_HOVER = 'linear-gradient(135deg, #4A3FD4 0%, #9333EA 100%)'

/**
 * Quiet surface wash for every arc screen with exposed background (AR-578).
 * Same lilac/peach family as ARC_HERO_WASH, but far more discreet: the base
 * tone #F7F8FC stays dominant — it is the background BOTH the SPA and the
 * monolith share (see `_arc_branding.scss` in mosaic-backend; keep in sync)
 * so cross-document navigation never flashes. Each glow alpha-fades to zero
 * of its own hue with an intermediate stop to avoid banding.
 * Novalumi (multi-brand): derive per-brand from that brand's hero palette.
 */
export const ARC_SURFACE_WASH =
  'radial-gradient(ellipse 85% 65% at 12% -8%, rgba(237, 233, 254, 0.55) 0%, ' +
  'rgba(237, 233, 254, 0.28) 32%, rgba(237, 233, 254, 0) 62%), ' +
  'radial-gradient(ellipse 75% 55% at 90% -4%, rgba(255, 241, 230, 0.5) 0%, ' +
  'rgba(255, 241, 230, 0.26) 30%, rgba(255, 241, 230, 0) 58%), ' +
  '#F7F8FC'

/* ============================ End design tokens ============================ */

/** Gentle skeleton shimmer: narrow opacity range, slow ease-in-out cycle */
const arcSkeletonFade = keyframes({
  'from, to': { opacity: 0.55 },
  '50%': { opacity: 0.9 },
})

/**
 * Returns a new theme override with the Arc branding merged over `base`.
 * Pure: never mutates `base`. Everything not related to the branding
 * (radius, spacing, breakpoints, component styles) is preserved.
 */
export const arcThemeOverride = (base: MantineThemeOverride): MantineThemeOverride => ({
  ...base,
  colors: {
    ...base.colors,
    arcPurple: ARC_PURPLE_SHADES,
    // Hardcoded color='turquoise'/'teal' usages across the app resolve to the
    // brand ramp under Arc (filled = shade 6 #5B4FEB, hover = shade 7)
    turquoise: ARC_PURPLE_SHADES,
    teal: ARC_PURPLE_SHADES,
  },
  primaryColor: 'arcPurple',
  primaryShade: { light: ARC_PRIMARY_SHADE_INDEX, dark: ARC_PRIMARY_SHADE_INDEX },
  fontFamily: ARC_FONT_FAMILY,
  // Heading sizes/weights come from the base theme — only the family changes
  headings: { ...base.headings, fontFamily: ARC_FONT_FAMILY },
  // Brand wash behind all arc screens (AR-578): a fixed pseudo-element (NOT
  // background-attachment: fixed, which janks on Safari/iOS) paints
  // ARC_SURFACE_WASH behind every route, so any screen with exposed
  // background gets it for free; screens with their own opaque backdrop
  // (e.g. the /projects/new hero) simply cover it. Light scheme only — dark
  // keeps the legacy dark body. Keyed on html.arc-branding (toggled by
  // app.tsx together with this override) so it outranks the plain `body`
  // rule in MosaicoGlobalStyles regardless of emotion injection order.
  // Legacy schools never receive this override, so they stay byte-identical.
  globalStyles: theme => ({
    ...(base.globalStyles ? base.globalStyles(theme) : {}),
    ...(theme.colorScheme !== 'dark'
      ? {
        // Solid base behind the fixed wash (overscroll / pre-paint frames)
        'html.arc-branding body': { backgroundColor: '#F7F8FC' },
        'html.arc-branding body::before': {
          content: '""',
          position: 'fixed',
          inset: 0,
          zIndex: -1,
          pointerEvents: 'none',
          background: ARC_SURFACE_WASH,
        },
      }
      : {}),
  }),
  components: {
    ...base.components,
    // HIG skeletons: soft neutral tone with a subtle, slow shimmer instead of
    // the stock gray.3 hard pulse. Dimensions/radius stay per-usage so each
    // skeleton mirrors the component it replaces.
    Skeleton: {
      styles: (theme, params: { animate?: boolean }) => ({
        visible: {
          '&::after': {
            background: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2],
            animation:
              params.animate === false ? 'none' : `${arcSkeletonFade} 2200ms ease-in-out infinite`,
          },
        },
      }),
    },
    // Legacy theme pins Anchor to the old purple; re-pin links to the arc indigo
    Anchor: {
      defaultProps: {
        color: `arcPurple.${ARC_PRIMARY_SHADE_INDEX}`,
      },
      styles: theme => ({
        root: {
          '&:hover': {
            textDecoration: 'underline',
            color:
              theme.colorScheme === 'dark'
                ? theme.colors.arcPurple[4]
                : theme.colors.arcPurple[7],
          },
        },
      }),
    },
  },
})
